import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setWalletOpen, setLoginOpen, setRewardsOpen } from 'store/uiSlice';
import { ChevronLeftIcon, ChevronRightIcon } from 'components/icons';
import slider4 from 'assets/table.webp';
import slider5 from 'assets/originals.webp';
import slider6 from 'assets/deposit.webp';
import slider7 from 'assets/rewards.webp';
import slider8 from 'assets/transfer.webp';
import LevelTransferModal from './levelTransferModal';
import styles from './slider.module.scss';

const Slider: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLevelTransferModalOpen, setIsLevelTransferModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useLocalizedNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const slides = useMemo(
    () => [
      {
        id: 6,
        src: slider4,
        title: t('slider.tableGames.title', 'Table\nGames'),
        subtitle: t(
          'slider.tableGames.subtitle',
          'Classic Roulette, Blackjack & Baccarat.\nTake your seat at the premium tables.',
        ),
        buttonText: t('slider.tableGames.button', 'Play Table Games'),
        action: 'play_table_games',
      },
      {
        id: 4,
        src: slider6,
        title: t('slider.cryptoDeposits.title', 'Instant\nCrypto Deposits'),
        subtitle: t(
          'slider.cryptoDeposits.subtitle',
          'Lightning-fast payouts. Secure transactions.\nTop up your balance and play!',
        ),
        buttonText: t('common.depositNow', 'Deposit Now'),
        action: 'deposit',
      },
      {
        id: 5,
        src: slider5,
        title: t('slider.originalGames.title', 'Original\nGames'),
        subtitle: t(
          'slider.originalGames.subtitle',
          'Exclusive Provably Fair Games.\nPlay the next generation of casino classics.',
        ),
        buttonText: t('slider.originalGames.button', 'Play Originals'),
        action: 'play_originals',
      },
      {
        id: 7,
        src: slider7,
        title: t('slider.vipRewards.title', 'VIP\nRewards'),
        subtitle: t(
          'slider.vipRewards.subtitle',
          'Unlock exclusive bonuses, cashback, and rakeback.\nYour loyalty pays off.',
        ),
        buttonText: t('common.claimRewards', 'Claim Rewards'),
        action: 'play_rewards',
      },
      {
        id: 8,
        src: slider8,
        title: t('slider.levelTransfer.title', 'Transfer Your\nExisting Level'),
        subtitle: t(
          'slider.levelTransfer.subtitle',
          'Match your Level status from other platforms.\nProof of an existing level may be required.',
        ),
        buttonText: t('common.details', 'Details'),
        action: 'level_transfer',
      },
    ],
    [t],
  );

  const handleSlideAction = (action?: string) => {
    if (action === 'deposit') {
      if (isAuthenticated) {
        dispatch(setWalletOpen(true));
      } else {
        dispatch(setLoginOpen(true));
      }
    } else if (action === 'play_originals') {
      navigate('/casino/categories/originals');
    } else if (action === 'play_table_games') {
      navigate('/casino/categories/table_games');
    } else if (action === 'play_rewards') {
      dispatch(setRewardsOpen(true));
    } else if (action === 'level_transfer') {
      setIsLevelTransferModalOpen(true);
    }
  };

  // Auto-loop every 5 seconds
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(timer);
  }, [isPaused]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className={styles.sliderContainer}>
      {/* Slides */}
      <div
        className={styles.slidesWrapper}
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide, index) => {
          const isFirst = index === 0;
          return (
            <div key={slide.id} className={styles.slide}>
              <img
                src={slide.src}
                fetchPriority={isFirst ? 'high' : 'auto'}
                loading={isFirst ? 'eager' : 'lazy'}
                alt="promo"
                className={styles.slideImage}
              />
              <div className={styles.slideContent}>
                <h2 className={styles.slideTitle}>{slide.title}</h2>
                {slide.subtitle && <p className={styles.slideSubtitle}>{slide.subtitle}</p>}
                <button
                  className={
                    slide.id === 4
                      ? styles.depositBtn
                      : slide.id === 5
                        ? styles.origamiBtn
                        : slide.id === 6
                          ? styles.tableGamesBtn
                          : slide.id === 7
                            ? styles.rewardsBtn
                            : slide.id === 8
                              ? styles.transferBtn
                              : styles.slideBtn
                  }
                  onClick={() => handleSlideAction(slide.action)}
                >
                  {slide.buttonText}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows & Dots */}
      <div
        className={styles.controlsContainer}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className={styles.arrows}>
          <button className={styles.arrowBtn} onClick={goToPrev} aria-label="Previous slide">
            <ChevronLeftIcon width="20" height="20" />
          </button>
          <button className={styles.arrowBtn} onClick={goToNext} aria-label="Next slide">
            <ChevronRightIcon width="20" height="20" />
          </button>
        </div>

        <div className={styles.dots}>
          {slides.map((_, idx) => (
            <span
              key={idx}
              className={`${styles.dot} ${idx === currentIndex ? styles.dotActive : ''}`}
            ></span>
          ))}
        </div>
      </div>
      <LevelTransferModal
        isOpen={isLevelTransferModalOpen}
        onClose={() => setIsLevelTransferModalOpen(false)}
      />
    </div>
  );
};

export default Slider;
